/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Timeout } from './Common';
import { Logger } from './Logger';

/**
 * Settings for detecting times when the timeouts were not called - such as computer sleeping,
 * where we want to reset the backoff afterwards
 */
export type GapDetectionSettings = {
    /** backoff will be reset if the detected wait was this times the expected wait */
    tolerance: number,

    /** minimum wait after which backoff will be reset, to avoid spurious resets due to lack of timer precision */
    minimumWaitMs: number
}

/**
 * Executes callbacks with exponential backoff
 */
export class ExponentialBackoff {
    private backoffId = 0;
    private backoffCount = 0;
    private timerHandle?: Timeout;
    private callback?: (waitStart?: number, expectedWait?: number) => void;
    private previousCompleteTime?: number;

    public constructor(
        private logger: Logger,
        private maxBackoffInMs: number,
        private gapDetectionSettings: GapDetectionSettings = { tolerance: 2, minimumWaitMs: 10_000 }
    ) { }

    private static calculateNextBackoffMs(backoffCount: number, maxBackoffInMs: number): number {
        const fudge = 1 + (Math.random() - 0.5) * 0.4; // +/- 20%
        let backoff = 1000 * Math.pow(2, backoffCount) * fudge; // 1s initial
        backoff = Math.round(backoff);

        return Math.min(maxBackoffInMs, backoff);
    }

    public setMaxBackoffMs(ms: number) {
        this.maxBackoffInMs = ms;
    }

    public backoff(taskName: string, handler: () => void): void {
        if (this.timerHandle !== undefined) {
            this.logger.debug('Clearing current back off');
            clearTimeout(this.timerHandle);
            this.timerHandle = undefined;
        }

        // This detects a situation where the computer/tab likely went to sleep while NOT waiting on backoff,
        // but doing other things (e.g. waiting on fetch, waiting on token provider, etc.),
        // and woke up much later, the operation having failed, triggering a retry after backoff.
        // If a lot of time has passed in sleep, we want to reset that backoff.
        //
        // We are counting on the fact that the operation (fetch, token wait, etc.) should never take significantly longer
        // than the maximum allowed backoff time, to avoid unwanted resets.
        //
        // (If computer woke up while it WAS waiting on backoff, after a long time, it would likely complete that wait
        // immediately, so previousCompleteTime would be very close to the next attempt, thus not triggering this.)
        if (this.previousCompleteTime !== undefined) {
            const elapsed = Date.now() - this.previousCompleteTime;
            // The time to reset this needs to be much longer here, as the operation itself can take quite a long time even under normal circumstances.
            // In practice, based on settings as of February 2024, this can be triggered after a sleep that in some cases needs to be over 10 minutes.
            if (elapsed > this.maxBackoffInMs * this.gapDetectionSettings.tolerance && elapsed > this.gapDetectionSettings.minimumWaitMs) {
                this.logger.info(`Back off for ${taskName} with ID ${this.backoffId} will be reset due to a lot of time having passed since the previous backoff (${elapsed} ms)`);
                this.backoffCount = 0;
                this.previousCompleteTime = undefined;
            }
        }

        const backoffMs = ExponentialBackoff.calculateNextBackoffMs(this.backoffCount, this.maxBackoffInMs);
        this.backoffId++;
        this.backoffCount++;
        this.logger.info(`Backing off ${taskName} for ${backoffMs} milliseconds with ID ${this.backoffId}`);
        this.callback = (waitStart?: number, expectedWait?: number) => {
            if (shouldReset(waitStart, expectedWait, this.gapDetectionSettings)) {
                // Most likely computer/tab went to sleep while waiting on backoff and woke up much later
                this.logger.info(`Back off for ${taskName} with ID ${this.backoffId} will be reset due to the wait (${Date.now() - (waitStart ?? 0)} ms) being longer than expected (${expectedWait} ms)`);
                this.backoffCount = 0;
            }
            this.logger.info(`Back off for ${taskName} with ID ${this.backoffId} complete, invoking handler`);
            this.timerHandle = undefined;
            this.callback = undefined;
            this.previousCompleteTime = Date.now();
            handler();
        };
        const backoffWaitStart = Date.now();
        this.timerHandle = setTimeout(
            () => this.callback?.(backoffWaitStart, backoffMs),
            backoffMs
        );
    }

    /// Stop the backoff if active, do not call the handler and reset to the initial/shortest period again
    public reset(): void {
        if (this.timerHandle !== undefined) {
            this.logger.debug(`Resetting back off with ID ${this.backoffId}`);
            clearTimeout(this.timerHandle);
            this.timerHandle = undefined;
            this.callback = undefined;
        }
        this.backoffCount = 0;
    }

    /// Call the handler right now if active and reset to the initial/shortest period again
    public expediteIfPending(): void {
        this.backoffCount = 0;
        if (this.timerHandle !== undefined) {
            this.logger.debug(`Expediting back off with ID ${this.backoffId}`);
            clearTimeout(this.timerHandle);
            this.timerHandle = undefined;
            const callback = this.callback;
            this.callback = undefined;
            if (callback) {
                callback();
            }
        }
    }
}

function shouldReset(waitStart: number|undefined, expectedWait: number|undefined, settings: GapDetectionSettings): boolean {
    if (waitStart === undefined || expectedWait === undefined) {
        return false;
    }
    const actualWait = Date.now() - waitStart;
    return actualWait > expectedWait * settings.tolerance && actualWait > settings.minimumWaitMs;
}



// WEBPACK FOOTER //
// ./lib_temp/ExponentialBackoff.ts